/**
 * The backend's error codes, and what the dashboard says for each.
 *
 * Every error the backend shows a person carries a snake_case code in
 * `body.detail` (never a sentence), which the dashboard translates into the
 * person's language. Anything else (FastAPI's 422 list, a plain-text 500, a
 * 502 or 504 page from the proxy, a network error) gets the action's own
 * generic message instead.
 */

import type { ApiError } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import type { nl } from "@/lib/i18n/locales/nl";

/** Every code the backend sends in `body.detail` (its `ErrorCode`). */
export const API_ERROR_CODES = [
  // Session and access
  "not_authenticated",
  "invalid_session",
  "session_revoked",
  "account_not_found",
  "admin_required",
  "owner_required",
  "developer_required",
  "view_as_read_only",
  "view_as_self",
  "view_as_ended",
  "auth_unavailable",
  // Users
  "revoke_failed",
  "user_not_found",
  "invalid_role",
  "cannot_demote_self",
  "cannot_delete_self",
  "cannot_revoke_self",
  "role_assignment_forbidden",
  "user_change_forbidden",
  // Tools and service
  "tool_not_found",
  "tool_not_switchable",
  // Teams and exceptions
  "team_not_found",
  "team_name_taken",
  "team_not_deletable",
  "team_not_editable",
  "team_member_not_allowed",
  "person_not_allowed",
  "unit_unknown",
  "app_not_for_default_team",
  "admins_team_not_deletable",
  "admins_team_not_editable",
  "service_unavailable",
  // Apps
  "app_access_required",
  "snapshot_not_found",
  "section_not_found",
  "job_not_found",
  "recompute_running",
  "recompute_too_soon",
  "odoo_not_configured",
  "snapshot_outdated",
  "invalid_range",
  "detail_not_found",
  "client_not_found",
  // Roadmap
  "roadmap_item_not_found",
  "roadmap_forbidden",
  "roadmap_transition_not_allowed",
  "roadmap_estimate_required",
  "roadmap_waits_loop",
  "roadmap_unknown_person",
  "roadmap_linear_id_taken",
  "roadmap_effort_not_a_step",
  "roadmap_blocker_not_found",
  "roadmap_blocker_invalid",
  "roadmap_item_done",
  "roadmap_mention_invalid",
  "roadmap_comment_not_found",
  "roadmap_triage_not_found",
  "roadmap_triage_closed",
  // Issues
  "tool_issue_not_found",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** The action a generic message names: "Failed to delete user.", … */
export type ApiErrorFallback = keyof typeof nl.errors.fallback;

const KNOWN_CODES: ReadonlySet<string> = new Set(API_ERROR_CODES);

export function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === "string" && KNOWN_CODES.has(value);
}

/**
 * The code of a failed API call, or null. Only an HTTP error response carries
 * one: a network error (status 0) holds the browser's own English message in
 * `detail`, never a code.
 */
export function apiErrorCode(error: unknown): ApiErrorCode | null {
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (typeof status !== "number" || status < 400 || typeof body !== "object" || body === null) {
    return null;
  }
  const detail = (body as { detail?: unknown }).detail;
  return isApiErrorCode(detail) ? detail : null;
}

/**
 * What to tell a person about a failed API call, in their language: the
 * code's message, else the generic message of the action that failed.
 */
export function apiErrorMessage(error: unknown, fallbackKey: ApiErrorFallback): string {
  const code = apiErrorCode(error);
  return code ? i18n.t(`errors.codes.${code}`) : i18n.t(`errors.fallback.${fallbackKey}`);
}

/**
 * The codes the API client answers with a toast, whichever request got them:
 * a change refused while a developer views the dashboard as someone else
 * (`view_as_read_only`), and a view that ended (`view_as_ended`, said after
 * the reload it brings).
 */
const TOASTED_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  "view_as_read_only",
  "view_as_ended",
]);

/** Whether a toast already says this failure, so no form, dialog or page says it again. */
export function isToastedError(error: unknown): boolean {
  const code = apiErrorCode(error);
  return code !== null && TOASTED_CODES.has(code);
}

/**
 * A failure as a form, a dialog or a page shows it: as it is, or null when a
 * toast already says it (`isToastedError`), so nothing shows. The form stays
 * as it was, ready to try again.
 */
export function inlineFailure<const T extends { error: unknown }>(failure: T | null): T | null {
  return failure !== null && isToastedError(failure.error) ? null : failure;
}
